import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { BarrierDirection, Bbox, LatLonPoint } from '@/types/barriers';

export interface BarrierDraft {
  readonly point: LatLonPoint;
  readonly pointLabel: string | null;
  readonly name: string;
  readonly comment: string;
  readonly radius_m: number;
  readonly direction: BarrierDirection;
  readonly direction_hint: readonly LatLonPoint[] | null;
  readonly auto_waypoints: readonly LatLonPoint[];
}

interface BarrierWorkspaceValue {
  readonly draft: BarrierDraft | null;
  readonly selectedId: number | null;
  /**
   * Bbox to fly the map to. Set by table-row focus so the map navigates
   * to the chosen barrier; consumed by `MainMap` via `fitBounds`. The
   * bbox is recomputed (new array reference) on every focus call so the
   * effect re-runs even when revisiting the same barrier.
   */
  readonly focusedBarrierBbox: Bbox | null;
  /**
   * Per-click signal for "fly the map to this exact waypoint". The token
   * is monotonically increasing so re-clicking the same waypoint still
   * triggers the MainMap effect (selection-style identity comparisons
   * would no-op).
   */
  readonly focusedWaypoint: Readonly<{
    lat: number;
    lon: number;
    token: number;
  }> | null;
  readonly startDraft: (
    point: LatLonPoint,
    roadName: string | null,
    defaultRadiusM: number,
  ) => void;
  readonly updateDraftFields: (
    patch: Partial<Omit<BarrierDraft, 'point' | 'pointLabel'>>,
  ) => void;
  readonly setDraftHintEndpoint: (endpoint: LatLonPoint) => void;
  readonly addDraftWaypoint: (point: LatLonPoint) => void;
  readonly addDraftWaypoints: (points: readonly LatLonPoint[]) => void;
  readonly removeDraftWaypoint: (index: number) => void;
  readonly cancelDraft: () => void;
  readonly selectBarrier: (id: number | null) => void;
  /** Selects + signals the map to fly to the barrier. */
  readonly focusBarrier: (id: number, bbox: Bbox) => void;
  /** Pans/zooms the map to a single waypoint without changing selection. */
  readonly flyToWaypoint: (waypoint: LatLonPoint) => void;
}

const BarrierWorkspaceContext = createContext<BarrierWorkspaceValue | undefined>(undefined);

const DEFAULT_DIRECTION: BarrierDirection = 'both';

interface BarrierWorkspaceProviderProps {
  readonly children: ReactNode;
}

export function BarrierWorkspaceProvider({ children }: BarrierWorkspaceProviderProps) {
  const [draft, setDraft] = useState<BarrierDraft | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [focusedBarrierBbox, setFocusedBarrierBbox] = useState<Bbox | null>(null);
  const [focusedWaypoint, setFocusedWaypoint] = useState<
    Readonly<{ lat: number; lon: number; token: number }> | null
  >(null);

  const startDraft = useCallback(
    (point: LatLonPoint, roadName: string | null, defaultRadiusM: number) => {
      setSelectedId(null);
      setDraft({
        point,
        pointLabel: roadName,
        name: '',
        comment: '',
        radius_m: defaultRadiusM,
        direction: DEFAULT_DIRECTION,
        direction_hint: null,
        auto_waypoints: [],
      });
    },
    [],
  );

  const updateDraftFields = useCallback(
    (patch: Partial<Omit<BarrierDraft, 'point' | 'pointLabel'>>) => {
      setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    },
    [],
  );

  const setDraftHintEndpoint = useCallback((endpoint: LatLonPoint) => {
    setDraft((prev) => {
      if (!prev) {
        return prev;
      }
      return { ...prev, direction_hint: [prev.point, endpoint] };
    });
  }, []);

  const addDraftWaypoint = useCallback((point: LatLonPoint) => {
    setDraft((prev) => {
      if (!prev) {
        return prev;
      }
      return { ...prev, auto_waypoints: [...prev.auto_waypoints, point] };
    });
  }, []);

  const addDraftWaypoints = useCallback((points: readonly LatLonPoint[]) => {
    if (points.length === 0) return;
    setDraft((prev) => {
      if (!prev) return prev;
      // De-duplicate by 5-decimal grid (~1 m at the equator) against
      // both the existing list and within the new batch. Two consecutive
      // auto-detect calls on the same barrier + radius would otherwise
      // double the waypoints, forcing the user to hand-delete each.
      const seen = new Set(prev.auto_waypoints.map(_gridKey));
      const merged = [...prev.auto_waypoints];
      for (const p of points) {
        const key = _gridKey(p);
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(p);
      }
      if (merged.length === prev.auto_waypoints.length) return prev;
      return { ...prev, auto_waypoints: merged };
    });
  }, []);

  const removeDraftWaypoint = useCallback((index: number) => {
    setDraft((prev) => {
      if (!prev) {
        return prev;
      }
      const next = prev.auto_waypoints.filter((_, i) => i !== index);
      return { ...prev, auto_waypoints: next };
    });
  }, []);

  const cancelDraft = useCallback(() => {
    setDraft(null);
  }, []);

  const selectBarrier = useCallback((id: number | null) => {
    setDraft(null);
    setSelectedId(id);
    if (id === null) {
      setFocusedBarrierBbox(null);
    }
  }, []);

  const focusBarrier = useCallback((id: number, bbox: Bbox) => {
    setDraft(null);
    setSelectedId(id);
    // Always create a fresh tuple so MainMap's `fitBounds` effect re-runs
    // even when the user re-clicks the row that's already selected.
    setFocusedBarrierBbox([bbox[0], bbox[1], bbox[2], bbox[3]]);
  }, []);

  const flyToWaypoint = useCallback((waypoint: LatLonPoint) => {
    // Token guarantees the MainMap effect fires on every call, even when
    // the same waypoint is clicked twice in a row.
    setFocusedWaypoint({
      lat: waypoint.lat,
      lon: waypoint.lon,
      token: Date.now(),
    });
  }, []);

  const value = useMemo<BarrierWorkspaceValue>(
    () => ({
      draft,
      selectedId,
      focusedBarrierBbox,
      focusedWaypoint,
      startDraft,
      updateDraftFields,
      setDraftHintEndpoint,
      addDraftWaypoint,
      addDraftWaypoints,
      removeDraftWaypoint,
      cancelDraft,
      selectBarrier,
      focusBarrier,
      flyToWaypoint,
    }),
    [
      draft,
      selectedId,
      focusedBarrierBbox,
      focusedWaypoint,
      startDraft,
      updateDraftFields,
      setDraftHintEndpoint,
      addDraftWaypoint,
      addDraftWaypoints,
      removeDraftWaypoint,
      cancelDraft,
      selectBarrier,
      focusBarrier,
      flyToWaypoint,
    ],
  );

  return (
    <BarrierWorkspaceContext.Provider value={value}>
      {children}
    </BarrierWorkspaceContext.Provider>
  );
}

export function useBarrierWorkspace(): BarrierWorkspaceValue {
  const ctx = useContext(BarrierWorkspaceContext);
  if (!ctx) {
    throw new Error('useBarrierWorkspace must be used within BarrierWorkspaceProvider');
  }
  return ctx;
}

function _gridKey(p: LatLonPoint): string {
  return `${p.lat.toFixed(5)}:${p.lon.toFixed(5)}`;
}
